/**
 * ECIES over ristretto255: seal a message to a public point so that only the holder of the
 * secret scalar, or a threshold of its shareholders, can open it.
 *
 *   r ← random, R = r·G, S = r·PK (= sk·R)
 *   KEK = HKDF(S, salt = R ‖ PK, info)
 *   box = XChaCha20-Poly1305(KEK, aad)
 */
import { open as aeadOpen, seal as aeadSeal } from "./aead.js";
import { concat } from "./bytes.js";
import { assertPoint, basePointMul, pointMul, randomScalar, scalarFromBytes } from "./group.js";
import { INFO, derive } from "./kdf.js";
export function kekFromShared(shared, ephemeral, publicKey) {
    assertPoint(shared);
    return derive(shared, concat(ephemeral, publicKey), INFO.ecies);
}
export function sealTo(publicKey, plaintext, aad) {
    const r = randomScalar();
    const R = basePointMul(r);
    const kek = kekFromShared(pointMul(publicKey, r), R, publicKey);
    return { ephemeral: R, box: aeadSeal(kek, plaintext, aad) };
}
/** Direct open with the full secret scalar. Not used for sealed media after a vault is sealed. */
export function openWith(secretKey, publicKey, sealed, aad) {
    const kek = kekFromShared(pointMul(sealed.ephemeral, scalarFromBytes(secretKey)), sealed.ephemeral, publicKey);
    return aeadOpen(kek, sealed.box, aad);
}
/** Open given an already-combined shared point S = sk·R (see threshold.ts). */
export function openWithShared(sharedPoint, publicKey, sealed, aad) {
    const kek = kekFromShared(sharedPoint, sealed.ephemeral, publicKey);
    return aeadOpen(kek, sealed.box, aad);
}
export function encodeSealed(s) {
    return concat(s.ephemeral, s.box);
}
export function decodeSealed(b) {
    if (b.length < 32 + 24 + 16)
        throw new Error("sealed blob too short");
    return { ephemeral: b.slice(0, 32), box: b.slice(32) };
}
//# sourceMappingURL=ecies.js.map